import {
  DAY,
  HOUR,
  isDefined,
  type Geo200Id,
  type LocationActivity,
  type LocationLoadInput,
  type LocationLoadSuccess,
  type ResponseError,
  toResponseError,
} from '@terros/common'
import { useQueries } from '@tanstack/react-query'
import { locationLoadKey } from '../../keys'
import { useServerRequest } from '../../api'

type LocationLoadState = {
  data: LocationActivity[]
  isLoading: boolean
  isFetching: boolean
  error?: ResponseError
  refetch: () => void
}

type LocationLoadResult = {
  data?: LocationActivity[]
  error: unknown
  isLoading: boolean
  isFetching: boolean
  refetch: () => void
}

export function combineLocationLoads(results: LocationLoadResult[]): LocationLoadState {
  const failed = results.find((result) => isDefined(result.error))
  return {
    data: results
      .map((result) => result.data)
      .filter(isDefined)
      .flat(),
    isLoading: results.some((result) => result.isLoading),
    isFetching: results.some((result) => result.isFetching),
    error: failed ? toResponseError(failed.error) : undefined,
    refetch: () => results.forEach((result) => result.refetch()),
  }
}

export function useLocationLoads(geo200Ids: Geo200Id[], options?: { enabled?: boolean }): LocationLoadState {
  const serverRequest = useServerRequest<LocationLoadInput, LocationLoadSuccess>('/location/load')

  return useQueries({
    queries: geo200Ids.map((geo200Id) => ({
      queryKey: locationLoadKey(geo200Id),
      queryFn: async (): Promise<LocationActivity[]> => {
        const res = await serverRequest({ geo200Id, extended: false })
        return res.locations
      },
      staleTime: HOUR,
      gcTime: DAY,
      enabled: options?.enabled ?? true,
    })),
    combine: combineLocationLoads,
  })
}
